import { useState } from "react";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { confirmPasswordReset, requestPasswordReset } from "../auth/cognito";
import type { LoginResult } from "../auth/cognito";

export function LoginPage() {
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingChallenge, setPendingChallenge] = useState<LoginResult | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"login" | "forgot_request" | "forgot_confirm">("login");
  const [code, setCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  // Every challenge step resolves a LoginResult, so one function advances the
  // chain wherever it came from -- initial login, the temporary-password
  // panel, or an enrollment verify. Duplicating this per call site is how one
  // path silently loses a case.
  const advance = async (result: LoginResult) => {
    if (result.type === "success") {
      await completeLogin(result.tokens, email);
      navigate("/");
      return;
    }
    setPendingChallenge(result);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await advance(await login(email, password));
    } catch (err) {
      const name = (err as { name?: string; code?: string })?.name
        ?? (err as { code?: string })?.code;
      // Credential failures stay deliberately generic -- no user enumeration.
      // prevent_user_existence_errors = ENABLED makes an unknown email and a
      // wrong password indistinguishable at Cognito, and both arrive here as
      // NotAuthorizedException, so one message covers them honestly.
      //
      // Everything else is NOT a credential problem, and saying it is sends
      // people off to reset a password that was always fine. With MFA
      // required, a failure in the setup or challenge step lands here too --
      // including the one that bit us in dev, where a browser holding a
      // cached index.html loaded a bundle with no MFA_SETUP handler and the
      // SDK failed straight to onFailure.
      const credentials = name === "NotAuthorizedException"
        || name === "UserNotFoundException";
      setError(credentials
        ? "Invalid email or password."
        : "Could not complete sign-in. Reload the page (Shift+Reload) and try again — "
          + "if it persists, ask an administrator.");
    } finally {
      setBusy(false);
    }
  };

  const onSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingChallenge?.type !== "new_password_required") return;
    setError(null);
    setBusy(true);
    try {
      await advance(await pendingChallenge.completeNewPassword(newPassword));
    } catch {
      setError("Could not set that password. Try a longer one with a mix of cases and numbers.");
    } finally {
      setBusy(false);
    }
  };

  const NOT_ACTIVATED =
    "This account hasn't been activated yet — ask your administrator to resend your invitation.";

  const onRequestReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await requestPasswordReset(email);
      setNotice("If that email has an account, a reset code is on its way.");
      setMode("forgot_confirm");
    } catch (err) {
      // A user still holding a temporary password cannot reset it -- Cognito
      // refuses, and the only way forward is an admin resending the invite.
      const name = (err as { name?: string; code?: string })?.name
        ?? (err as { code?: string })?.code;
      setError(name === "NotAuthorizedException" ? NOT_ACTIVATED : "Could not send a reset code.");
    } finally {
      setBusy(false);
    }
  };

  const onConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await confirmPasswordReset(email, code, newPassword);
      setNotice("Password updated. Log in with your new password.");
      setMode("login");
      setCode("");
      setNewPassword("");
    } catch {
      setError("That code was not accepted. Check it and try a longer password.");
    } finally {
      setBusy(false);
    }
  };

  const TOTP_MISMATCH =
    "That code didn't match. Check your authenticator and try again.";

  const onVerifyEnrollment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingChallenge?.type !== "mfa_setup") return;
    setError(null);
    setBusy(true);
    try {
      await advance(await pendingChallenge.verify(totpCode));
    } catch {
      setError(TOTP_MISMATCH);
    } finally {
      setBusy(false);
      setTotpCode("");
    }
  };

  const onSubmitTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pendingChallenge?.type !== "totp_required") return;
    setError(null);
    setBusy(true);
    try {
      await advance(await pendingChallenge.submit(totpCode));
    } catch {
      setError(TOTP_MISMATCH);
    } finally {
      setBusy(false);
      setTotpCode("");
    }
  };

  if (pendingChallenge?.type === "mfa_setup") {
    const otpauth = `otpauth://totp/${encodeURIComponent(`Scadox Portal:${email}`)}`
      + `?secret=${pendingChallenge.secretCode}&issuer=Scadox`;
    return (
      <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
        <h2>Set up your authenticator</h2>
        <p className="dim">
          Scan this with an authenticator app, then enter the code it shows.
        </p>
        {/* Explicit white ground: the generated SVG is black on transparent,
            and the card is not guaranteed light enough for a scanner. */}
        <div style={{ background: "#fff", padding: 12, width: "fit-content" }}>
          <QRCode value={otpauth} size={160} />
        </div>
        <p className="dim">Or enter this key by hand:</p>
        {/* The secret is ~52 unbroken base32 characters, which is wider than
            this card. Without break-all it renders as one line and overflows
            the frame -- and it has no spaces or hyphens, so normal word
            wrapping has nowhere to break. select-all makes one click grab the
            whole key, since it is meant to be copied into another app. */}
        <code style={{
          display: "block",
          wordBreak: "break-all",
          fontSize: "0.85em",
          lineHeight: 1.6,
          margin: "0 0 12px",
          userSelect: "all",
        }}>{pendingChallenge.secretCode}</code>
        <form onSubmit={onVerifyEnrollment}>
          <input placeholder="6-digit code" value={totpCode} inputMode="numeric"
                 onChange={(e) => setTotpCode(e.target.value)} required />
          {error && <p style={{ color: "var(--red)" }}>{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>
            Verify and finish
          </button>
        </form>
      </div>
    );
  }

  if (pendingChallenge?.type === "totp_required") {
    return (
      <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
        <h2>Enter your authenticator code</h2>
        <form onSubmit={onSubmitTotp}>
          <input placeholder="6-digit code" value={totpCode} inputMode="numeric"
                 onChange={(e) => setTotpCode(e.target.value)} required />
          {error && <p style={{ color: "var(--red)" }}>{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>Verify</button>
        </form>
      </div>
    );
  }

  if (pendingChallenge?.type === "new_password_required") {
    return (
      <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
        <h2>Set your permanent password</h2>
        <form onSubmit={onSetPassword}>
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
          {error && <p style={{ color: "var(--red)" }}>{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>Set password</button>
        </form>
      </div>
    );
  }

  if (mode === "forgot_request") {
    return (
      <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
        <h2>Reset your password</h2>
        <form onSubmit={onRequestReset}>
          <input type="email" placeholder="Email" value={email}
                 onChange={(e) => setEmail(e.target.value)} required />
          {error && <p style={{ color: "var(--red)" }}>{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>Send reset code</button>
          <button type="button" disabled={busy}
                  onClick={() => { setMode("login"); setError(null); setNotice(null); }}>
            Back to login
          </button>
        </form>
      </div>
    );
  }

  if (mode === "forgot_confirm") {
    return (
      <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
        <h2>Enter your reset code</h2>
        {notice && <p className="dim">{notice}</p>}
        <form onSubmit={onConfirmReset}>
          <input placeholder="Code from your email" value={code}
                 onChange={(e) => setCode(e.target.value)} required />
          <input type="password" placeholder="New password" value={newPassword}
                 onChange={(e) => setNewPassword(e.target.value)} required />
          {error && <p style={{ color: "var(--red)" }}>{error}</p>}
          <button className="btn-primary" type="submit" disabled={busy}>Set new password</button>
          <button type="button" disabled={busy}
                  onClick={() => { setMode("login"); setError(null); setNotice(null); }}>
            Back to login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="card" style={{ maxWidth: 360, margin: "80px auto" }}>
      <h2>SCAD<span style={{ color: "var(--teal)" }}>OX</span> Portal</h2>
      <form onSubmit={onSubmit}>
        <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        {notice && <p className="dim">{notice}</p>}
        {error && <p style={{ color: "var(--red)" }}>{error}</p>}
        <button className="btn-primary" type="submit" disabled={busy}>Log in</button>
        <button type="button" onClick={() => { setMode("forgot_request"); setError(null); setNotice(null); }}>
          Forgot password?
        </button>
      </form>
    </div>
  );
}
